import { logEvent, Analytics } from "firebase/analytics";
import { getFirebaseAnalytics } from "./firebase";
import { getUserProfile } from "@/utils/jwtHelper";

export enum ItemAction {
  LOG = "log",
  CREATE = "create",
  READ = "read",
  ANALYZE = "analyze",
  VIEW_ANALYTICS = "view_analytics",
  EDIT = "edit",
  DELETE = "delete",
  DELETE_PERMANENTLY = "delete_permanently",
  RESTORE = "restore",
  DOWNLOAD = "download",
  UPLOAD = "upload",
  DUPLICATE = "duplicate",
  CREATE_SHORTCUT = "create_shortcut",
  MOVE = "move",
  SHARE = "share",
  UNSHARE = "unshare",
  UPDATE_PERMISSION = "update_permission",
  ASSIGN_ROLE = "assign_role",
  REMOVE_ROLE = "remove_role",
}

export const ANALYTICS_EVENT = "item_action";

type CommonFields = {
  item_id: string;
  item_type: "file" | "folder";
  status: "success" | "fail";
  user_id?: string;
  error_message?: string;
};

type CreateParams = CommonFields & {
  action: ItemAction.CREATE;
  parent_id: string;
  source?: "upload_folder";
};

type UploadParams = CommonFields & {
  action: ItemAction.UPLOAD;
  parent_id: string;
  source?: "upload_folder";
};

type AnalyzeParams = CommonFields & {
  action: ItemAction.ANALYZE;
  workflow_type?: string;
  workflow_title?: string;
};

type ShareParams = CommonFields & {
  action: ItemAction.SHARE;
  target_user_id: string;
};

type UnshareParams = CommonFields & {
  action: ItemAction.UNSHARE;
  target_user_id: string;
};

type PermissionParams = CommonFields & {
  action: ItemAction.UPDATE_PERMISSION;
  target_user_id: string;
  permissions: string[];
};

type RoleParams = CommonFields & {
  action: ItemAction.ASSIGN_ROLE | ItemAction.REMOVE_ROLE;
  target_user_id: string;
};

type LogParams = {
  action: ItemAction.LOG;
  log_level: "debug" | "log" | "info" | "warn" | "error";
  message: string;
  user_id?: string;
  error_message?: string;
};

type BasicParams = CommonFields & {
  action: Exclude<
    ItemAction,
    | ItemAction.LOG
    | ItemAction.CREATE
    | ItemAction.UPLOAD
    | ItemAction.SHARE
    | ItemAction.UNSHARE
    | ItemAction.UPDATE_PERMISSION
    | ItemAction.ASSIGN_ROLE
    | ItemAction.REMOVE_ROLE
  >;
};

export type AnalyticsParams =
  | LogParams
  | CreateParams
  | UploadParams
  | AnalyzeParams
  | ShareParams
  | UnshareParams
  | PermissionParams
  | RoleParams
  | BasicParams;

export const trackEvent = (params: AnalyticsParams) => {
  const analytics: Analytics | null = getFirebaseAnalytics();
  if (!analytics) return;

  const userId = getUserProfile()?.Id || "unknown";
  
  logEvent(analytics, ANALYTICS_EVENT, {
    ...params,
    user_id: userId,
  });
};
